import React, { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LoginRequest, LoginResponse, isValidEmail, PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH } from '@df-gate-share/shared';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, getErrorMessage } from '../lib/api';
import { GOOGLE_CLIENT_ID, MICROSOFT_CLIENT_ID, DFGATE_CLIENT_ID } from '../lib/env';
import { generateOidcSecurityParams, OIDC_STORAGE_KEYS } from '../lib/oidcSecurity';
import MkdfLogo from '../components/MkdfLogo';
import DFFilesLogo from '../components/DFFilesLogo';

interface LoginFormData {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email) {
      newErrors.email = 'メールアドレスを入力してください';
    } else if (!isValidEmail(formData.email)) {
      // CR-041: メール検証は shared の唯一定義を使用
      newErrors.email = '正しいメールアドレスを入力してください';
    }

    if (!formData.password) {
      newErrors.password = 'パスワードを入力してください';
    } else if (
      // CR-051: 長さ範囲を shared の一元ポリシーに統一
      formData.password.length < PASSWORD_MIN_LENGTH ||
      formData.password.length > PASSWORD_MAX_LENGTH
    ) {
      newErrors.password = `パスワードは${PASSWORD_MIN_LENGTH}〜${PASSWORD_MAX_LENGTH}文字で入力してください`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setApiError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const requestBody: LoginRequest = {
        email: formData.email,
        password: formData.password
      };

      const response = await apiClient.post<LoginResponse>('/api/v1/auth/login', requestBody);

      const { jwt, user } = response.data;

      login(jwt, user);

      if (user.requirePasswordChange) {
        navigate('/password-change/required');
      } else {
        navigate('/files');
      }

    } catch (error) {
      setApiError(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const OIDC_AUTH_ENDPOINTS: Record<'google' | 'microsoft' | 'dfgate', string> = {
    google: 'https://accounts.google.com/o/oauth2/v2/auth',
    microsoft: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    dfgate: 'https://product-develop.mk-df.com/auth/realms/df-gate-login/protocol/openid-connect/auth',
  };

  const handleOIDCLogin = async (provider: 'google' | 'microsoft' | 'dfgate') => {
    const clientId = provider === 'google'
      ? GOOGLE_CLIENT_ID
      : provider === 'microsoft' ? MICROSOFT_CLIENT_ID : DFGATE_CLIENT_ID;

    const redirectUri = `${window.location.origin}/oidc-callback`;
    const scope = 'openid email profile';

    // CR-006: CSRF(state) / リプレイ(nonce) / 認可コード横取り(PKCE) 対策パラメータを生成し、
    // sessionStorage に保存（コールバックで突合・利用）
    const { state, nonce, codeVerifier, codeChallenge } = await generateOidcSecurityParams();
    sessionStorage.setItem(OIDC_STORAGE_KEYS.state, state);
    sessionStorage.setItem(OIDC_STORAGE_KEYS.nonce, nonce);
    sessionStorage.setItem(OIDC_STORAGE_KEYS.codeVerifier, codeVerifier);
    sessionStorage.setItem(OIDC_STORAGE_KEYS.provider, provider);

    const authUrl = `${OIDC_AUTH_ENDPOINTS[provider]}?` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code&` +
      `scope=${encodeURIComponent(scope)}&` +
      `state=${encodeURIComponent(state)}&` +
      `nonce=${encodeURIComponent(nonce)}&` +
      `code_challenge=${encodeURIComponent(codeChallenge)}&` +
      `code_challenge_method=S256`;

    window.location.href = authUrl;
  };

  return (
    <div className="hero-gradient">
      <div className="hero-card">
        <div className="hero-card__header">
          <div className="hero-card__logo">
            <MkdfLogo height={28} />
            <span className="header__brand-divider" style={{ margin: '0 8px', opacity: 0.3 }}>|</span>
            <DFFilesLogo size={24} variant="light" />
          </div>
          <div className="hero-card__tagline">セキュアなファイル共有ソリューション</div>
        </div>

        <div className="hero-card__body">
          {apiError && (
            <div className="alert alert--error">{apiError}</div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email" className="form-label">メールアドレス</label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={isLoading}
                placeholder="example@company.com"
                className={`form-input ${errors.email ? 'form-input--error' : ''}`}
              />
              {errors.email && <p className="form-error">{errors.email}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">パスワード</label>
              <input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                disabled={isLoading}
                placeholder="パスワードを入力"
                autoComplete='current-password'
                className={`form-input ${errors.password ? 'form-input--error' : ''}`}
              />
              {errors.password && <p className="form-error">{errors.password}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn btn--primary btn--lg btn--block mb-4"
            >
              {isLoading ? 'ログイン中...' : 'ログイン'}
            </button>

            <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
              <Link to="/password-reset" style={{ fontSize: '0.875rem' }}>パスワードを忘れた場合</Link>
            </div>
          </form>

          <div className="divider">
            <span className="divider__text">または</span>
          </div>

          <div className="flex flex-col gap-3">
            {/* Google・Microsoftログインは一旦未サポート */}

            <button
              onClick={() => handleOIDCLogin('dfgate')}
              disabled={isLoading}
              className="btn btn--secondary btn--block"
            >
              <img style={{ width: '20px', height: '20px' }} src="df-gate-icon.png" alt="" />
              DF GATEでログイン
            </button>
          </div>

          <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
            <a
              href="/docs/index.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '0.8125rem', color: '#6b7280', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>help_outline</span>
              操作マニュアル
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
